import React, { useEffect, useState, useCallback } from 'react';

import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import LoadingSpinner from '~/shared/components/LoadingSpinner';
import { deepArrayFlatten } from '~/shared/utils/helpers';
import { Router } from '~/techstyle-shared/next-routes';
import {
  useAccountActions,
  useReviewableProductDetails,
  useReviewableProducts,
} from '~/techstyle-shared/react-accounts';

import { trackSubmitReviewClick } from '../../actions';
import ReviewBrowser from '../ReviewBrowser';

// Business Logic

const allowSubmit = (reviewData, reviewPages) => {
  // Iterate through the list of supplied questions,
  // see which ones are required,
  // then determine if we have a supplied answer for it

  const reviewOptionsList = reviewPages?.map((reviewPage) =>
    reviewPage.fields?.filter((field) => field.required)
  );

  const submittedKeys = Object.keys(reviewData);

  if (!reviewOptionsList) {
    return false;
  }

  const isListOptionAnswered = !deepArrayFlatten(...reviewOptionsList).some(
    (question) => {
      // If question is required and it doesn't exist in the submitted keys
      const questionKey = `${question.reviewTemplateFieldId}`;

      const required = question.required;
      const hasBeenSubmitted = submittedKeys.includes(questionKey);
      const thisItem = reviewData[questionKey];
      let hasError = false;
      if (thisItem && thisItem.error) {
        hasError = true;
      }

      return required && !(hasBeenSubmitted && !hasError);
    }
  );

  return isListOptionAnswered;
};

const WriteReviewLayout = ({
  handleCancel,
  filteredProducts,
  setFilteredProducts,
  initialProduct,
  productReviewId,
}) => {
  const { reviewableProducts } = useReviewableProducts();
  const [product, setProduct] = useState(initialProduct || null);
  const [masterProductId, setMasterProductId] = useState(
    initialProduct?.masterProductId || null
  );

  const [loading, setLoading] = useState(true);
  const [reviewData, setReviewData] = useState({});
  const [photoData, setPhotoData] = useState(null);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isError, setError] = useState(false);
  const { saveProductReview, saveProductReviewImage } = useAccountActions();

  const { reviewPages, networkStatus } =
    useReviewableProductDetails(masterProductId);

  const reviewIsEmpty = isEmpty(reviewData);

  const handleChange = useCallback((changedData) => {
    setReviewData((prevReviewData) => {
      return {
        ...prevReviewData,
        [changedData.id]: changedData,
      };
    });
  }, []);

  const handlePhotoChange = (attachData) => {
    setPhotoData({ attachData });
  };

  const handleCancelOrFallback =
    handleCancel ||
    (() => {
      // Route to go back to reviews list
      Router.push('/account/reviews', { scroll: true });
    });

  useEffect(() => {
    if (reviewableProducts) {
      setLoading(false);
      let product = initialProduct;
      if (!product) {
        product = reviewableProducts.find(
          (product) => product.id === productReviewId
        );
      }
      if (!product || !product.masterProductId) {
        setError(true);
        return;
      }
      setProduct(product);
      setMasterProductId(product.masterProductId);
    }
  }, [reviewableProducts, productReviewId, initialProduct]);

  const validation = useCallback((reviewDetails = [{ fields: [] }]) => {
    const formReviewFieldTemplateIdRequired = deepArrayFlatten(
      ...reviewDetails.map((reviewPage) =>
        reviewPage.fields?.filter((field) => field.required)
      )
    );

    const formReviewFieldTemplateId = formReviewFieldTemplateIdRequired.map(
      (reviewField) => reviewField.reviewTemplateFieldId
    );

    const error = formReviewFieldTemplateId.some(
      (id) => !formReviewFieldTemplateIdRequired[id]
    );

    setError(error);
    return error;
  }, []);

  const dispatch = useDispatch();
  const handleProcessReview = async () => {
    const { productId, orderId } = product;
    const reviewTemplateId = reviewPages[0].reviewTemplateId;
    const formData = {};
    const keys = Object.keys(reviewData);

    for (let i = 0; i < keys.length; i++) {
      const reviewId = keys[i];
      const key = `review_template_field_${reviewId}`;

      const value =
        reviewData[reviewId].reviewTemplateFieldAnswerId ||
        reviewData[reviewId].value;

      formData[key] = value;
    }

    const processForm = validation(reviewPages ?? undefined);

    if (processForm) {
      // API to submit
      const params = {
        productId,
        pageNumber: 1,
        reviewId: 0,
        reviewTemplateId,
        allowReviewUpdate: true,
        orderId,
        formData,
        points: 20,
      };
      setLoading(true);
      setIsSuccessful(true);
      if (filteredProducts) {
        setFilteredProducts(
          filteredProducts.filter((product) => product.productId !== productId)
        );
      }
      dispatch(trackSubmitReviewClick());

      try {
        await saveProductReview(params);
        if (photoData) {
          await saveProductReviewImage(productId, photoData.attachData);
        }
        handleCancelOrFallback();
        setLoading(false);
      } catch (err) {
        throw new Error(err.message);
      }
    }
  };

  const disableSubmit = allowSubmit(reviewData, reviewPages);

  if (loading && !reviewPages && !networkStatus.isUpToDate) {
    return <LoadingSpinner center />;
  }

  return (
    <ReviewBrowser
      handleChange={handleChange}
      handleCancel={handleCancelOrFallback}
      handlePhotoChange={handlePhotoChange}
      handleProcessReview={handleProcessReview}
      product={product}
      isDisabled={disableSubmit}
      isSuccessful={isSuccessful}
      options={reviewPages ?? []}
      validate={() => validation(reviewPages ?? undefined)}
      isEmpty={reviewIsEmpty}
      isError={isError}
    />
  );
};

WriteReviewLayout.propTypes = {
  filteredProducts: PropTypes.array,
  handleCancel: PropTypes.func,
  initialProduct: PropTypes.object,
  productReviewId: PropTypes.number,
  setFilteredProducts: PropTypes.func,
};

export default WriteReviewLayout;
